import {
  isCallMePhoneProvisioned,
  isSmsProvisioned,
} from "~/lib/marketplace/agent-phone-provisioned";
import {
  isEmailInboxProvisioned,
  type AgentEmailInboxFields,
} from "~/lib/marketplace/agent-email-provisioned";
import { isMessengerChannelLive } from "~/lib/channels/messenger-availability";
import { isWhatsAppChannelLive } from "~/lib/channels/whatsapp-availability";
import type { SidebarWidgetTab } from "~/app/components/landing-agent-sidebar";
import {
  AI_EMPLOYEE_PLANS,
  type AiEmployeePlanId,
} from "~/lib/siteglue/ai-employee-plans";
import {
  planAllowsFeature,
  type PlanFeatureKey,
} from "~/lib/siteglue/plan-presets";

export type IndustryLiveDemoTabKey =
  | "aiChat"
  | "sms"
  | "email"
  | "contact"
  | "talk"
  | "call"
  | "videoCall"
  | "welcomeVideo"
  | "whatsapp"
  | "messenger";

export type IndustryLiveDemoTab = {
  key: IndustryLiveDemoTabKey;
  label: string;
  widgetTab: SidebarWidgetTab;
  disabledPlaceholder: string;
};

/** Taller iframe for industry landing live demos (was 520/560px). */
export const INDUSTRY_LIVE_DEMO_IFRAME_CLASS =
  "h-[min(720px,75vh)] min-h-[600px] w-full border-0 sm:h-[min(780px,80vh)]";

export const INDUSTRY_LIVE_DEMO_PLACEHOLDER_MIN_H = "min-h-[600px] sm:min-h-[680px]";

/** Left→right Test / Live Demo channel order. */
export const INDUSTRY_LIVE_DEMO_TABS: IndustryLiveDemoTab[] = [
  {
    key: "aiChat",
    label: "Web Chat",
    widgetTab: "aiChat",
    disabledPlaceholder:
      "Website chat is not enabled for this demo agent. Enable Live Chat in your agent settings to preview it here.",
  },
  {
    key: "talk",
    label: "Voice (WebRTC)",
    widgetTab: "talk",
    disabledPlaceholder:
      "Voice (WebRTC) is not enabled for this demo agent. Enable Voice (WebRTC) in your agent settings to preview it here.",
  },
  {
    key: "contact",
    label: "Contact Form",
    widgetTab: "contact",
    disabledPlaceholder:
      "The contact form is not enabled for this demo agent. Enable Contact Form in your agent settings to preview it here.",
  },
  {
    key: "call",
    label: "Phone Calls",
    widgetTab: "call",
    disabledPlaceholder:
      "Phone Calls are not enabled for this demo agent. Enable Phone Calls and provision a number to preview it here.",
  },
  {
    key: "sms",
    label: "Text Messages",
    widgetTab: "sms",
    disabledPlaceholder:
      "Text Messages are not enabled for this demo agent. Enable Text Messages in your agent settings to preview it here.",
  },
  {
    key: "email",
    label: "Email",
    widgetTab: "email",
    disabledPlaceholder:
      "Email is not enabled for this demo agent. Enable Email and provision an AgentEmail inbox in your agent settings to preview it here.",
  },
  {
    key: "welcomeVideo",
    label: "Welcome Video",
    widgetTab: "welcomeVideo",
    disabledPlaceholder:
      "Welcome Video is not enabled for this demo agent. Enable Welcome Video in your agent settings to preview it here.",
  },
  {
    key: "videoCall",
    label: "Video Calls",
    widgetTab: "videoCall",
    disabledPlaceholder:
      "Video Calls are not enabled for this demo agent. Enable Video Calls in your agent settings to preview it here.",
  },
  {
    key: "whatsapp",
    label: "WhatsApp",
    widgetTab: "whatsapp",
    disabledPlaceholder:
      "WhatsApp is not enabled for this demo agent. Enable WhatsApp in your agent settings to preview it here.",
  },
  {
    key: "messenger",
    label: "Messenger",
    widgetTab: "messenger",
    disabledPlaceholder:
      "Facebook Messenger is not enabled for this demo agent. Enable Messenger in your agent settings to preview it here.",
  },
];

const PLAN_ORDER: Exclude<AiEmployeePlanId, "enterprise">[] = [
  "intern",
  "associate",
  "professional",
];

/** Plan feature that gates each Test tab (null = not plan-gated). */
export function planFeatureForLiveDemoTab(
  tabKey: IndustryLiveDemoTabKey,
): PlanFeatureKey | null {
  switch (tabKey) {
    case "talk":
      return "browserCalls";
    case "call":
      return "phoneCalls";
    case "email":
      return "email";
    case "sms":
      return "textMessages";
    case "whatsapp":
      return "whatsapp";
    case "messenger":
      return "messenger";
    case "videoCall":
    case "welcomeVideo":
      return "videoCalls";
    case "aiChat":
    case "contact":
    default:
      return null;
  }
}

export function minimumPlanForLiveDemoTab(
  tabKey: IndustryLiveDemoTabKey,
): Exclude<AiEmployeePlanId, "enterprise"> | null {
  // Test-tab copy: dedicated email upgrades to Professional (not Associate).
  if (tabKey === "email") return "professional";

  const feature = planFeatureForLiveDemoTab(tabKey);
  if (!feature) return null;
  for (const planId of PLAN_ORDER) {
    if (planAllowsFeature(planId, feature)) return planId;
  }
  return "professional";
}

const PLAN_GATED_CHANNEL_COPY: Partial<
  Record<
    IndustryLiveDemoTabKey,
    { noun: string; detail: string }
  >
> = {
  talk: {
    noun: "Voice (WebRTC)",
    detail: "Voice (WebRTC) is not enabled",
  },
  call: {
    noun: "Phone Calls",
    detail: "Dedicated AI employee phone number is not enabled",
  },
  email: {
    noun: "Email",
    detail: "Dedicated AI employee email address is not enabled",
  },
  sms: {
    noun: "Text Messages",
    detail: "Text Messages are not enabled",
  },
  whatsapp: {
    noun: "WhatsApp",
    detail: "WhatsApp messaging is not enabled",
  },
  messenger: {
    noun: "Messenger",
    detail: "Facebook Messenger is not enabled",
  },
  videoCall: {
    noun: "Video Calls",
    detail: "Video Calls are not enabled",
  },
  welcomeVideo: {
    noun: "Welcome Video",
    detail: "Welcome Video is not enabled",
  },
};

export type PlanGatedLiveDemoPlaceholder = {
  title: string;
  message: string;
  upgradePlanId: Exclude<AiEmployeePlanId, "enterprise">;
  upgradeLabel: string;
};

/**
 * When the current plan does not include this channel, return upgrade copy.
 * Example: Email on Intern → "EMAIL NOT ENABLED FOR INTERN PLAN" + upgrade CTA.
 */
export function planGatedLiveDemoPlaceholder(
  tabKey: IndustryLiveDemoTabKey,
  currentPlanId: AiEmployeePlanId | null | undefined,
): PlanGatedLiveDemoPlaceholder | null {
  const feature = planFeatureForLiveDemoTab(tabKey);
  const requiredPlanId = minimumPlanForLiveDemoTab(tabKey);
  if (!feature || !requiredPlanId) return null;

  const current = currentPlanId ?? "intern";
  if (planAllowsFeature(current, feature)) return null;

  const currentName =
    AI_EMPLOYEE_PLANS.find((p) => p.id === current)?.name ?? "Intern";
  const upgradeName =
    AI_EMPLOYEE_PLANS.find((p) => p.id === requiredPlanId)?.name ?? "Professional";
  const copy = PLAN_GATED_CHANNEL_COPY[tabKey];
  const label = copy?.noun ?? "Channel";

  return {
    title: `${label} not enabled for ${currentName} plan`,
    message: `${copy?.detail ?? `${label} is not enabled`} for ${currentName} plan. Click below to upgrade to ${upgradeName} Plan.`,
    upgradePlanId: requiredPlanId,
    upgradeLabel: `Upgrade to ${upgradeName}`,
  };
}

type ChannelFlags = {
  aiChat?: boolean;
  talk?: boolean;
  contact?: boolean;
  call?: boolean;
  sms?: boolean;
  email?: boolean;
  whatsapp?: boolean;
  messenger?: boolean;
};

type PhoneAgentShape = {
  vapiAiPhoneId?: string | null;
  smsEnabled?: boolean | null;
} | null;

export type IndustryLiveDemoVisualFlags = {
  talkAnamVideoEnabled?: boolean | null;
  welcomeVideoEnabled?: boolean | null;
};

/** Whether a live demo tab can load the widget iframe for this project. */
export function isIndustryLiveDemoTabEnabled(
  tabKey: IndustryLiveDemoTabKey,
  channels: ChannelFlags | null | undefined,
  phoneAgent: PhoneAgentShape,
  notificationSettings?: AgentEmailInboxFields | null,
  visualFlags?: IndustryLiveDemoVisualFlags | null,
): boolean {
  if (!channels) return tabKey === "aiChat";

  switch (tabKey) {
    case "aiChat":
      return channels.aiChat !== false;
    case "contact":
      return channels.contact === true;
    case "sms":
      return channels.sms === true && isSmsProvisioned(phoneAgent);
    case "email":
      return channels.email === true && isEmailInboxProvisioned(notificationSettings);
    case "talk":
      return channels.talk === true;
    case "call":
      return channels.call === true && isCallMePhoneProvisioned(phoneAgent);
    case "videoCall":
      return channels.talk === true && visualFlags?.talkAnamVideoEnabled === true;
    case "welcomeVideo":
      return visualFlags?.welcomeVideoEnabled === true;
    case "whatsapp":
      return isWhatsAppChannelLive(channels);
    case "messenger":
      return isMessengerChannelLive(channels);
    default:
      return false;
  }
}

export function buildIndustryLiveDemoWidgetSrc(
  projectId: string,
  widgetTab: SidebarWidgetTab,
  sessionId?: string | null,
): string {
  const params = new URLSearchParams({
    full: "true",
    scroll: "top",
    embeddedSidebar: "1",
    initialTab: widgetTab,
  });
  if (sessionId) {
    params.set("sessionId", sessionId);
  }
  return `/w/${projectId}?${params.toString()}`;
}
